// src/lib/api/Reports/notifications/notifications-history-api-call.ts
import store from '../../../../store/store';
import { RequestInfo } from '../../../../interfaces/RequestInfo';
import { CENTRAL_API_BASE_URL_V2 } from '../../../constants/central-api';
import { asyncHandler } from '../../async-handler';

/* ------------------------- Interfaces (typed) ------------------------- */

export type NotificationsExecutionStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export interface NotificationsExecutionMetadata {
  // New structure fields (v2)
  version?: string;
  graphs_count?: number;
  prompts_count?: number;
  insights_count?: number;
  platforms_fetched?: string[];
  processing_method?: string;
  api_response_status?: string;
  tara_insights_count?: number;
  external_insights_count?: number;

  // Common fields
  n8n_delivery_triggered?: boolean;
  n8n_delivery_triggered_at?: string | null;

  // Old structure fields (legacy)
  triggered_by?: string;
  command_options?: Record<string, any>;
  processing_mode?: string;
}

export interface NotificationsExecutionHistoryItem {
  id: string;
  status: NotificationsExecutionStatus;
  error_message: string | null;
  batch_identifier: string;
  executed_date: string; // ISO date: YYYY-MM-DD
  executed_at: string; // ISO datetime
  metadata: NotificationsExecutionMetadata;
}

export interface NotificationsExecutionHistoryPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
  has_more_pages: boolean;
}

export interface NotificationsExecutionHistoryApiEnvelope {
  success: boolean;
  message: string;
  data: {
    data: NotificationsExecutionHistoryItem[];
    pagination: NotificationsExecutionHistoryPagination;
  };
}

export interface NotificationsExecutionHistoryResult {
  items: NotificationsExecutionHistoryItem[];
  pagination: NotificationsExecutionHistoryPagination;
}

/* ----------------------------- URL builder ---------------------------- */
/** Example: /api/v2/tara/shops/{slug}/insights/execution-history?per_page=5&page=1 */
const NOTIFICATIONS_HISTORY_API = (
  shopSlug: string,
  page: number,
  perPage: number
) =>
  `${CENTRAL_API_BASE_URL_V2}tara/shops/${shopSlug}/insights/execution-history?per_page=${perPage}&page=${page}`;

/* ------------------------------- API call ----------------------------- */

export default async function get_notifications_execution_history_api_call(
  page: number = 1,
  perPage: number = 5
): Promise<NotificationsExecutionHistoryResult | null> {
  try {
    // Validate required parameters
    if (!page || page < 1) {
      console.error('Invalid page number:', page);
      return null;
    }
    if (!perPage || perPage < 1) {
      console.error('Invalid perPage number:', perPage);
      return null;
    }

    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth?.authorization?.accessToken;
    const sellerToken = accessToken?.sellerToken;

    // Validate required data from store
    if (!shopSlug) {
      console.error('Shop slug is not available');
      return null;
    }

    if (!accessToken || !sellerToken) {
      console.error('Access token or seller token is not available');
      return null;
    }

    if (!accessToken.type) {
      console.error('Access token type is not available');
      return null;
    }

    const requestInfo: RequestInfo = {
      url: NOTIFICATIONS_HISTORY_API(shopSlug, page, perPage),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `${accessToken.type} ${sellerToken}`,
      },
    };

    const actionType = 'GET_NOTIFICATIONS_HISTORY';
    const customMsg = 'Notifications history retrieved successfully';

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );

    // Validate response structure
    if (!res || !res.data) {
      console.error('Invalid response structure from API');
      return null;
    }

    const response: NotificationsExecutionHistoryApiEnvelope = res.data;

    if (
      res.actionType === actionType &&
      response?.success === true &&
      response?.data
    ) {
      // Validate and sanitize response data
      const items = Array.isArray(response.data.data)
        ? response.data.data.filter((item) => item && item.id)
        : [];
      const pagination = response.data.pagination;

      // Validate pagination structure
      if (
        !pagination ||
        typeof pagination.total !== 'number' ||
        typeof pagination.last_page !== 'number'
      ) {
        console.error('Invalid pagination structure');
        return {
          items,
          pagination: {
            current_page: page,
            per_page: perPage,
            total: items.length,
            last_page: 1,
            from: items.length > 0 ? 1 : 0,
            to: items.length,
            has_more_pages: false,
          },
        };
      }

      return {
        items,
        pagination,
      };
    } else {
      console.error('API request failed or returned unsuccessful response');
      return null;
    }
  } catch (exception) {
    console.error(
      'Error during get notifications history request:',
      exception instanceof Error ? exception.message : exception
    );
    return null;
  }
}
